import { parseTag } from "@/features/machines/addressing";
import type { Machine } from "@/features/machines/types";
import type { DashboardWidget } from "./types";

/**
 * Seções do painel: cabeçalhos que agrupam cards.
 *
 * O NOME VEM DO OPERADOR, e só dele. Duas tentativas anteriores mostraram por
 * quê:
 *
 *  - Derivar do MODO ("todo `AIN` é processo, todo `MB` é elétrico") é a mesma
 *    inferência que fazia todo `v` ser velocidade: plausível na bancada,
 *    errada no cliente que ligou uma balança no Modbus.
 *  - Derivar da SEMÂNTICA declarada (Estados/Ritmos/Totais/Leituras) é
 *    honesto, mas numa máquina com um widget de cada tipo rende quatro
 *    cabeçalhos de um card — página comprida sem ganho nenhum. Cabia melhor
 *    lado a lado.
 *
 * Então: sem seção nomeada, GRADE PLANA — o layout de sempre, sem cabeçalho.
 * Agrupar é uma escolha, e uma escolha que só quem opera a linha sabe fazer.
 *
 * A seção também é o que faz a altura compacta do card de estado (proposta E)
 * pagar: três estados soltos entre gráficos encolhem e deixam buraco, porque
 * o CSS grid iguala a fileira pela maior; três estados na MESMA seção formam
 * uma fileira que é baixa.
 */
export interface WidgetSection {
  key: string;
  /** Slot do gateway; `null` para o grupo de cálculos (V{n}). */
  slotIndex: number | null;
  /** Nome dado pelo operador. `null` = grade plana, sem cabeçalho. */
  label: string | null;
  /** Nome do dispositivo, exibido só quando há mais de um gateway. */
  deviceLabel: string | null;
  widgets: DashboardWidget[];
}

/**
 * Particiona os widgets visíveis. Com mais de um gateway o dispositivo
 * continua sendo o corte EXTERNO; dentro dele, as seções nomeadas.
 *
 * A ordem das seções é a da PRIMEIRA APARIÇÃO na lista salva — assim ela
 * acompanha o arrasto do operador em vez de impor uma ordem alfabética que
 * ninguém pediu. Os cards sem seção formam um grupo inicial sem cabeçalho.
 *
 * A lista sai plana de propósito: a reordenação por arrasto opera sobre
 * `WidgetSection[]` e não precisa saber que existem dois eixos.
 */
export function groupWidgetsBySection(
  widgets: DashboardWidget[],
  machine: Machine,
): WidgetSection[] {
  const bySlot = new Map<number, DashboardWidget[]>();
  const calc: DashboardWidget[] = [];

  for (const widget of widgets) {
    const tag = parseTag(widget.source);
    if (!tag) {
      calc.push(widget);
      continue;
    }
    const list = bySlot.get(tag.slotIndex) ?? [];
    list.push(widget);
    bySlot.set(tag.slotIndex, list);
  }

  const slots = [...bySlot.keys()].sort((left, right) => left - right);
  const manyDevices = slots.length > 1;
  const sections: WidgetSection[] = [];

  for (const slotIndex of slots) {
    const slotWidgets = bySlot.get(slotIndex) ?? [];
    const slot = machine.slots.find((candidate) => candidate.slotIndex === slotIndex);
    const deviceLabel = manyDevices ? (slot?.alias ?? `Gateway ${slotIndex + 1}`) : null;

    // `Map` preserva a ordem de insercao: a primeira aparicao fixa a posicao
    // da secao, e a lista salva ja vem na ordem que o operador arrastou.
    const byName = new Map<string, DashboardWidget[]>();
    const loose: DashboardWidget[] = [];

    for (const widget of slotWidgets) {
      const name = widget.section?.trim();
      if (!name) {
        loose.push(widget);
        continue;
      }
      const list = byName.get(name) ?? [];
      list.push(widget);
      byName.set(name, list);
    }

    if (loose.length > 0) {
      sections.push({
        key: `slot-${slotIndex}-sem-secao`,
        slotIndex,
        label: null,
        deviceLabel,
        widgets: loose,
      });
    }

    for (const [name, list] of byName) {
      sections.push({
        key: `slot-${slotIndex}-sec-${name}`,
        slotIndex,
        label: name,
        deviceLabel,
        widgets: list,
      });
    }
  }

  if (calc.length > 0) {
    sections.push({
      key: "calc",
      slotIndex: null,
      label: "Cálculos",
      deviceLabel: null,
      widgets: calc,
    });
  }

  return sections;
}

/** Nomes de secao ja usados no painel, para o editor oferecer em vez de exigir digitacao. */
export function knownSectionNames(widgets: DashboardWidget[]): string[] {
  const seen = new Set<string>();
  for (const widget of widgets) {
    const name = widget.section?.trim();
    if (name) seen.add(name);
  }
  return [...seen].sort((left, right) => left.localeCompare(right, "pt-BR"));
}
